import type React from "react";
import { memo, useState } from "react";

import { faPhone } from "@fortawesome/pro-regular-svg-icons";
import { Box } from "@mui/material";
import { GridActionsCellItem, type GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import invariant from "ts-invariant";

import { useGetAppointmentsV1 } from "api/services/appointment/hooks";
import type { AppointmentV1 } from "api/services/appointment/schemas/appointment_v1";
import type { Medium } from "api/services/appointment/schemas/shared";
import { useProfileContext } from "contexts/ProfileContext";
import { completeAppointment } from "routes/dashboard/default/queries/completeAppointment";
import { removeAppointment } from "routes/dashboard/default/queries/removeAppointment";
import { ConfirmationPopup } from "shared/atoms/ConfirmationPopup";
import { PhoneNumber } from "shared/atoms/PhoneNumber";
import { CircleCheckIcon, CircleXmarkIcon } from "shared/icons";
import { EmptyResultOverlay } from "shared/molecules/EmptyResultOverlay/EmptyResultOverlay";
import { MUIDataGrid } from "shared/molecules/MUIDataGrid";
import { theme } from "theme";
import { useLocalizedDate } from "utils/date";
import { reportError } from "utils/errorReporting";
import { translateAppointmentMedium, translateAppointmentPurpose } from "utils/translations";

const isPhone = (medium: Medium): boolean => medium === "phone";

const getCountryByLocale = (locale?: string): string => {
  switch (locale) {
    case "en-US":
      return "USA";
    case "sv-SE":
      return "Sweden";
    default:
      return "";
  }
};

type Dialog = {
  id: number | null;
  show: boolean;
  status: "complete" | "remove" | null;
};

const initialDialogState: Dialog = {
  id: null,
  show: false,
  status: null,
};

export const paginationModel = { page: 0, pageSize: 10 };

export const UpcomingAppointments: React.FC = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { profile } = useProfileContext();
  const { format, parseISO, isValid } = useLocalizedDate();

  const [showDialog, setShowDialog] = useState<Dialog>(initialDialogState);
  const [dialogError, setDialogError] = useState<string | null>(null);

  invariant(profile);

  const { data: appointmentsV1 = [], isLoading, refetch } = useGetAppointmentsV1(profile.id);

  const country = getCountryByLocale(profile.locale);

  const updateAppointment = async () => {
    try {
      if (!showDialog.id || !showDialog.status) {
        return;
      }

      if (showDialog.status === "complete") {
        await completeAppointment(showDialog.id);
      } else if (showDialog.status === "remove") {
        await removeAppointment(showDialog.id);
      }

      refetch();
      closeDialog();
    } catch (e) {
      setDialogError(t("errors.generic"));
      if (e instanceof Error || typeof e === "string") {
        reportError("AvailabilityForm.tsx", e);
      }
    }
  };

  const closeDialog = () => {
    setShowDialog(initialDialogState);
    setDialogError(null);
  };

  const handleCellClick = ({ field, row }: { field: string; row: AppointmentV1 }) => {
    if (field === "actions") {
      return;
    }
    if (field === "medium" && isPhone(row.medium)) {
      window.location.href = `tel:${row.patient_number}`;
      return;
    }

    navigate(`/patients/${row.patient_id}`, {
      state: {
        pathname,
        text: "dashboard",
      },
    });
  };

  const columns: GridColDef[] = [
    {
      field: "date",
      headerName: t("messages.appointments.time"),
      valueGetter: (_value, row) => row.start_time,
      renderCell: ({ row }) => {
        const startTime = parseISO(row.start_time);
        const endTime = parseISO(row.end_time);

        if (!isValid(startTime)) {
          return null;
        }

        return (
          <Box display="flex" gap="16px" justifyContent="space-between">
            <Box data-testid="appointment-date">{format(startTime, "PP")}</Box>
            <Box display="flex" gap="2px">
              <Box fontWeight={theme.fontWeight.light}>{format(startTime, "p")}</Box>-
              <Box fontWeight={theme.fontWeight.light}>{isValid(endTime) ? format(endTime, "p") : ""}</Box>
            </Box>
          </Box>
        );
      },
      width: 250,
    },
    {
      field: "fullname",
      headerName: t("dashboard.tables.activity_logs.labels.patient"),
      valueGetter: (_value, row) => `${row.patient_first_name} ${row.patient_last_name}`,
      renderCell: ({ row }) => (
        <Box fontWeight={theme.fontWeight.medium}>
          {row.patient_first_name} {row.patient_last_name}
        </Box>
      ),
      flex: 2,
    },
    {
      field: "medium",
      headerName: t("messages.appointments.type"),
      renderCell: ({ row }) => (
        <Box
          data-testid={isPhone(row.medium) ? "phone" : "medium"}
          color={theme.colors.redesign.b100}
          fontWeight={theme.fontWeight.medium}
        >
          {isPhone(row.medium) ? (
            <PhoneNumber phone={row.patient_number} country={country} />
          ) : (
            translateAppointmentMedium(row.medium)
          )}
        </Box>
      ),
      flex: 1,
    },
    {
      field: "purpose",
      headerName: t("messages.appointments.purpose"),
      renderCell: ({ row }) => (
        <Box fontWeight={theme.fontWeight.light}>{translateAppointmentPurpose(row.purpose)}</Box>
      ),
      flex: 1,
    },
    {
      field: "actions",
      type: "actions",
      width: 80,
      getActions: params => [
        <GridActionsCellItem
          icon={<CircleCheckIcon />}
          label={t("admin_billing.buttons.complete")}
          disabled={params.row.state === "completed"}
          className={params.row.state === "completed" ? "completed-cell" : ""}
          onClick={event => {
            if (params.row.state !== "completed") {
              event.stopPropagation();
              setShowDialog({
                show: true,
                status: "complete",
                id: params.row.id,
              });
            }
          }}
        />,
        <GridActionsCellItem
          icon={<CircleXmarkIcon />}
          label={t("buttons.delete")}
          onClick={event => {
            event.stopPropagation();
            setShowDialog({
              show: true,
              status: "remove",
              id: params.row.id,
            });
          }}
        />,
      ],
    },
  ];

  return (
    <>
      <MUIDataGridWrapper>
        <MUIDataGrid
          rows={appointmentsV1}
          columns={columns}
          getRowId={row => row.id}
          initialState={{ pagination: { paginationModel } }}
          title={t("dashboard.tables.calls.title")}
          slots={{
            noRowsOverlay: () => <EmptyResultOverlay text={t("dashboard.tables.calls.empty")} icon={faPhone} />,
            noResultsOverlay: () => <EmptyResultOverlay />,
          }}
          localeText={{
            noRowsLabel: "",
            noResultsOverlayLabel: "",
          }}
          loading={isLoading}
          density="compact"
          onCellClick={handleCellClick}
          getCellClassName={params => {
            if (params.field === "actions" || params.row.state !== "completed") {
              return "";
            }
            return "completed-cell";
          }}
          disableRowSelectionOnClick
          notifications={appointmentsV1.filter(appointment => appointment.state !== "completed").length}
        />
      </MUIDataGridWrapper>

      <ConfirmationPopup
        isOpen={showDialog.show && Boolean(showDialog.id) && Boolean(showDialog.status)}
        title={
          showDialog.status === "complete"
            ? t("dashboard.tables.calls.complete.title")
            : t("dashboard.tables.calls.remove.title")
        }
        dataTestID={`upcoming-appointment-${showDialog.status}`}
        showCloseButton={false}
        error={dialogError ?? undefined}
        isLoading={isLoading}
        onConfirm={() => updateAppointment()}
        onCancel={closeDialog}
        onClickOutside={closeDialog}
      />
    </>
  );
});

export const MUIDataGridWrapper = styled.div`
  & .completed-cell * {
    text-decoration: line-through;
    opacity: 50%;
  }
`;
